import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { OFFICES, FAQS, BRAND } from "@/data/content";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import { PageAtmosphere, FloatChip, EyebrowPill } from "@/components/site/PageAtmosphere";

export default function Contact() {
    const { openDemo } = useOutletContext();
    const [openFaq, setOpenFaq] = useState(0);

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-20 pb-12 bg-warm">
                <PageAtmosphere />
                <FloatChip icon="fa-solid fa-clock" label="Reply within 1 business day" accent="#5FA88A" delay={0.3} className="absolute top-24 right-[6%]" />
                <FloatChip icon="fa-solid fa-location-dot" label="4 offices across India" accent="#D4B542" delay={0.6} className="absolute bottom-6 right-[16%]" />
                <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Reveal className="mb-6">
                            <EyebrowPill label="Contact us" accent="#2E7D6B" testid="contact-eyebrow" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                                Supercharge your
                                <br />
                                <span className="muted">customer conversations.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 text-intro text-slate-600 max-w-xl text-base md:text-lg">
                                Reach out for demos, pricing, or support — a specialist will respond within one business day.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <button onClick={openDemo} className="pill-dark" data-testid="contact-hero-book">
                                    Book a demo
                                </button>
                                <a href={`mailto:${BRAND.emailSales}`} className="pill-ghost" data-testid="contact-hero-email">
                                    <i className="fa-solid fa-envelope" /> {BRAND.emailSales}
                                </a>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.3} className="lg:col-span-5">
                        <div className="mesh-frame p-8">
                            <div className="glass-card p-6 space-y-4">
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                    Quick contact
                                </div>
                                <a
                                    href={`tel:${BRAND.phone.replace(/[^+\d]/g, "")}`}
                                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/70 transition-colors"
                                    data-testid="quick-phone"
                                >
                                    <div className="icon-sticker w-11 h-11 shrink-0" style={{ "--st-color": "#2E7D6B" }}>
                                        <i className="fa-solid fa-phone" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.15em] text-slate-500">Phone</div>
                                        <div className="text-slate-900 text-base font-medium">{BRAND.phone}</div>
                                    </div>
                                </a>
                                <a
                                    href={`mailto:${BRAND.emailSupport}`}
                                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/70 transition-colors"
                                    data-testid="quick-email"
                                >
                                    <div className="icon-sticker w-11 h-11 shrink-0" style={{ "--st-color": "#5FA88A" }}>
                                        <i className="fa-solid fa-envelope" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.15em] text-slate-500">Support</div>
                                        <div className="text-slate-900 text-base font-medium">{BRAND.emailSupport}</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* OFFICES */}
            <section className="py-16 bg-warm">
                <div className="container-x">
                    <Reveal>
                        <div className="max-w-3xl mb-12">
                            <div className="text-sm text-slate-500 mb-3">Global locations</div>
                            <h2 className="heading-section text-3xl md:text-5xl">
                                <span className="text-slate-400">Connect with our teams</span> across India.
                            </h2>
                        </div>
                    </Reveal>

                    <StaggerGroup className="grid md:grid-cols-2 gap-6">
                        {OFFICES.map((o) => (
                            <StaggerChild key={o.key}>
                                <motion.div
                                    whileHover={{ y: -3 }}
                                    transition={{ duration: 0.3 }}
                                    className="gleap-card p-8 h-full"
                                    data-testid={`office-${o.key}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                                {o.label}
                                            </div>
                                            <h3
                                                className="heading-stat text-3xl text-slate-900 mt-1"
                                            >
                                                {o.city}
                                            </h3>
                                        </div>
                                        <div className="icon-sticker w-11 h-11 shrink-0" style={{ "--st-color": "#235F52" }}>
                                            <i className="fa-solid fa-location-dot" />
                                        </div>
                                    </div>
                                    <p className="mt-5 text-slate-600 text-sm leading-relaxed">{o.address}</p>
                                    <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Phone</div>
                                            <a
                                                href={`tel:${o.phone.replace(/[^+\d]/g, "")}`}
                                                className="text-slate-900 hover:text-[#2E7D6B] transition-colors text-sm font-medium"
                                            >
                                                {o.phone}
                                            </a>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Email</div>
                                            <a
                                                href={`mailto:${o.email}`}
                                                className="text-slate-900 hover:text-[#2E7D6B] transition-colors text-sm font-medium break-all"
                                            >
                                                {o.email}
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 bg-warm">
                <div className="container-x max-w-5xl">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-3">
                            <div className="text-slate-500 text-sm sticky top-24">Common questions</div>
                        </div>
                        <div className="md:col-span-9">
                            {FAQS.map((f, i) => {
                                const isOpen = openFaq === i;
                                return (
                                    <div
                                        key={f.q}
                                        className="faq-row"
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        data-testid={`faq-${i}`}
                                    >
                                        <div className="faq-q">
                                            <span>{f.q}</span>
                                            <motion.i
                                                className="fa-solid fa-chevron-down text-slate-500 text-xs"
                                                animate={{ rotate: isOpen ? 180 : 0 }}
                                                transition={{ duration: 0.25 }}
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                                    style={{ overflow: "hidden" }}
                                                >
                                                    <div className="faq-a">{f.a}</div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-warm">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Instantly delight your visitors
                            <br />
                            <span className="muted">with an AI agent for your brand.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button onClick={openDemo} className="pill-dark" data-testid="contact-footer-cta">
                                Book a demo
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
